import clsx from 'clsx';
import { useMemo } from 'react';
import { IconButton } from 'src/components/Buttons';
import { Content, useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, SimpleTable, Stack } from 'src/core/ui';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { SaleLineItemModal } from 'src/modals/SaleDetails/components/PaymentSection/SaleLineItemModal';
import * as tableStyles from 'src/modals/SaleDetails/SaleDetailsTable.css';
import { DeleteIcon, IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { MarketplacePaymentLineType } from 'src/WebApiController';

import * as styles from './RetailSaleCheckoutStep.css';
import { useRetailLineItemOperations } from './RetailSaleCheckoutStep.hooks';

const getCurrencySymbol = (currencyCode: string) => {
  const parts = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? '$';
};

const formatAmount = (amount: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));

export const RetailSaleEarningsSection = ({
  disabled,
}: {
  disabled: boolean;
}) => {
  const earningsMsg = useContent(ContentId.Earnings);
  const chargeToday = useContent(ContentId.ChargeToday);

  const {
    marketplacePaymentLines,
    totalNetProceeds,
    currencyCode,
    isLineItemModalOpen,
    openLineItemModal,
    closeLineItemModal,
    onLineAmountChange,
    onLineRateChange,
    onSaveLineItem,
    onDeleteLineItem,
    totalAmount,
  } = useRetailLineItemOperations();

  const symbol = getCurrencySymbol(currencyCode);

  // Pull tax to the last
  const reorderedMarketplaceLines = useMemo(
    () =>
      (marketplacePaymentLines ?? []).toSorted(
        (a, b) =>
          Number(a.paymentLineType === MarketplacePaymentLineType.Tax) -
          Number(b.paymentLineType === MarketplacePaymentLineType.Tax)
      ),
    [marketplacePaymentLines]
  );

  return (
    <>
      <DetailSection name={earningsMsg}>
        <SectionContent numOfColumns={1}>
          <div className={tableStyles.tableWrapper}>
            <SimpleTable.Table className={tableStyles.table}>
              <SimpleTable.Thead>
                <SimpleTable.Tr className={tableStyles.tableHeaderRow}>
                  <SimpleTable.Th className={tableStyles.tableHeader}>
                    <Content id={ContentId.Overview} />
                  </SimpleTable.Th>
                  <SimpleTable.Th className={tableStyles.tableHeader}>
                    <Content id={ContentId.RatePercent} />
                  </SimpleTable.Th>
                  <SimpleTable.Th className={tableStyles.tableHeader}>
                    <Content id={ContentId.Reference} />
                  </SimpleTable.Th>
                  <SimpleTable.Th className={tableStyles.tableHeader}>
                    <Content id={ContentId.Amount} />
                  </SimpleTable.Th>
                  <SimpleTable.Th
                    className={clsx(tableStyles.tableHeader, styles.actionCell)}
                  />
                </SimpleTable.Tr>
              </SimpleTable.Thead>
              <SimpleTable.Tbody>
                {/* Proceeds row — display only, no rate */}
                <SimpleTable.Tr>
                  <SimpleTable.Td className={tableStyles.tableCell}>
                    <Content id={ContentId.Proceed} />
                  </SimpleTable.Td>
                  <SimpleTable.Td className={tableStyles.tableCell}>
                    &mdash;
                  </SimpleTable.Td>
                  <SimpleTable.Td className={tableStyles.tableCell}>
                    &mdash;
                  </SimpleTable.Td>
                  <SimpleTable.Td className={tableStyles.tableCell}>
                    <div className={styles.amountCell}>
                      <span>{symbol}</span>
                      <span>{formatAmount(totalNetProceeds ?? 0)}</span>
                    </div>
                  </SimpleTable.Td>
                  <SimpleTable.Td
                    className={clsx(tableStyles.tableHeader, styles.actionCell)}
                  />
                </SimpleTable.Tr>

                {/* Charge / credit line items — editable amount & rate */}
                {reorderedMarketplaceLines.map((line) => {
                  const lineSymbol = line.currency
                    ? getCurrencySymbol(line.currency)
                    : symbol;

                  // Tax is an auto-generated, read-only line (POS-8561): rate and amount are
                  // derived from the buyer's tax rate and the taxable total, so no inputs, no
                  // delete action.
                  if (line.paymentLineType === MarketplacePaymentLineType.Tax) {
                    return (
                      <SimpleTable.Tr key={line.marketplacePaymentLineId}>
                        <SimpleTable.Td className={tableStyles.tableCell}>
                          <Content id={ContentId.Tax} />
                        </SimpleTable.Td>
                        <SimpleTable.Td className={tableStyles.tableCell}>
                          {line.ratePercent != null ? (
                            `${line.ratePercent}%`
                          ) : (
                            <>&mdash;</>
                          )}
                        </SimpleTable.Td>
                        <SimpleTable.Td className={tableStyles.tableCell}>
                          &mdash;
                        </SimpleTable.Td>
                        <SimpleTable.Td className={tableStyles.tableCell}>
                          <div className={styles.amountCell}>
                            <span>{lineSymbol}</span>
                            <span>{formatAmount(line.amount)}</span>
                          </div>
                        </SimpleTable.Td>
                        <SimpleTable.Td
                          className={clsx(
                            tableStyles.tableHeader,
                            styles.actionCell
                          )}
                        />
                      </SimpleTable.Tr>
                    );
                  }

                  return (
                    <SimpleTable.Tr key={line.marketplacePaymentLineId}>
                      <SimpleTable.Td className={tableStyles.tableCell}>
                        <Content
                          id={
                            line.isCredit ? ContentId.Credit : ContentId.Charge
                          }
                        />
                      </SimpleTable.Td>

                      {/* Rate — editable, auto-updates amount */}
                      <SimpleTable.Td className={tableStyles.tableCell}>
                        <div className={styles.rateCell}>
                          <input
                            type="number"
                            className={styles.numberInput}
                            value={line.ratePercent ?? ''}
                            placeholder="—"
                            disabled={disabled}
                            step="any"
                            onChange={(e) => {
                              const raw = e.target.value;
                              const rate = raw === '' ? null : parseFloat(raw);
                              onLineRateChange(
                                line,
                                rate == null || isNaN(rate) ? null : rate
                              );
                            }}
                          />
                          <span>%</span>
                        </div>
                      </SimpleTable.Td>

                      <SimpleTable.Td className={tableStyles.tableCell}>
                        {line.externalPaymentId || <>&mdash;</>}
                      </SimpleTable.Td>

                      {/* Amount — editable, auto-updates rate */}
                      <SimpleTable.Td className={tableStyles.tableCell}>
                        <div
                          className={clsx(styles.amountCell, {
                            [styles.amountCellCredit]: line.isCredit,
                          })}
                        >
                          <span>{lineSymbol}</span>
                          <input
                            type="number"
                            className={styles.numberInput}
                            value={Math.abs(line.amount) || ''}
                            placeholder="0.00"
                            step="any"
                            min="0"
                            disabled={disabled}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              onLineAmountChange(line, val);
                            }}
                          />
                        </div>
                      </SimpleTable.Td>

                      <SimpleTable.Td
                        className={clsx(
                          tableStyles.tableHeader,
                          styles.actionCell
                        )}
                      >
                        <IconButton
                          onClick={() =>
                            onDeleteLineItem(line.marketplacePaymentLineId)
                          }
                          disabled={disabled}
                          icon={
                            <DeleteIcon
                              withHoverEffect
                              fill={IconsFill.currentColor}
                              size={vars.iconSize.l}
                            />
                          }
                          titleContentId={ContentId.Remove}
                        />
                      </SimpleTable.Td>
                    </SimpleTable.Tr>
                  );
                })}
              </SimpleTable.Tbody>
            </SimpleTable.Table>
          </div>

          <Button
            variant="outline"
            className={styles.addLineItemButton}
            onClick={openLineItemModal}
          >
            <PlusIcon size={vars.iconSize.m} fill={IconsFill.currentColor} />
            <Content id={ContentId.AddChargeOrCredit} />
          </Button>

          <Stack
            justifyContent="spaceBetween"
            alignItems="center"
            className={styles.chargeTodaySummary}
          >
            <span>{chargeToday}</span>
            <span>{formatCurrency(totalAmount, currencyCode)}</span>
          </Stack>
        </SectionContent>
      </DetailSection>

      <SaleLineItemModal
        isOpen={isLineItemModalOpen}
        onClose={closeLineItemModal}
        onSave={onSaveLineItem}
        currencyCode={currencyCode}
      />
    </>
  );
};
